import * as React from "react";
import { values } from "lodash";
import { connect } from "react-redux";

import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { MultiButtonQuestion, YesOption } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import * as questionIds from "@teamprevent/common/healthSurveillance/questions/questionIds";
import { HealthSurveillanceSectionProps } from "@teamprevent/common/healthSurveillance/models";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";

const sectionId = "AssociateMedicalDetails";

// eslint-disable-next-line max-len
const AssociateMedicalDetailsSectionUnconnected: React.FunctionComponent<HealthSurveillanceSectionProps> = (props: HealthSurveillanceSectionProps) => {
    const questions = values(props.questions || {});
    const areFurtherDetailsMandatory: boolean = [
        getAnswerToQuestionWithId(questions, questionIds.IfAnsweredYesToAnyQuestionsAreSymptomsOngoingForLongerThan12Months),
        getAnswerToQuestionWithId(questions, questionIds.ConfirmThereHasBeenNoChangeToVisionOrOngoingMedicalConditionSinceLastF2FPitPupAssessment),
        getAnswerToQuestionWithId(questions, questionIds.AreAnySymptomsOrMedicationNewWithinLast12Months)
    ].findIndex(answer => answer === YesOption) !== -1;

    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            id={sectionId}
            title="Questions For the Associate"
        >
            <h4>Have you ever suffered from or are you currently suffering from any of the following medical conditions or medical problems</h4>
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HeartProblems),
                    getQuestionConfig(props.questionConfigs, questionIds.HeartProblemsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HighBloodPressure),
                    getQuestionConfig(props.questionConfigs, questionIds.HighBloodPressureFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.StokeOrTransientIschaemicAttacks),
                    getQuestionConfig(props.questionConfigs, questionIds.StokeOrTransientIschaemicAttacksFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HeadInjuryRequiringHospital),
                    getQuestionConfig(props.questionConfigs, questionIds.HeadInjuryRequiringHospitalFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.MetabolicDisorders),
                    getQuestionConfig(props.questionConfigs, questionIds.MetabolicDisordersFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HistoryOfBlackouts),
                    getQuestionConfig(props.questionConfigs, questionIds.HistoryOfBlackoutsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.SleepDisorders),
                    getQuestionConfig(props.questionConfigs, questionIds.SleepDisordersFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.JointProblems),
                    getQuestionConfig(props.questionConfigs, questionIds.JointProblemsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.NeurologicalDisorders),
                    getQuestionConfig(props.questionConfigs, questionIds.NeurologicalDisordersFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.NeurologicalDisorders) === MultiButtonQuestion.YesOption &&
                <>
                    { /* eslint-disable max-len */}
                    <MultiButtonQuestion questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalDisordersMedication)} readonly={props.readonly} />
                    <TextQuestion questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalDisordersLastEpisode)} readonly={props.readonly} />
                    <MultiButtonQuestion questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ConditionsThatAffectBalance)} readonly={props.readonly} />
                    { /* eslint-enable max-len */}
                </>}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.Headaches),
                    getQuestionConfig(props.questionConfigs, questionIds.HeadachesFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HearingDifficulty),
                    getQuestionConfig(props.questionConfigs, questionIds.HearingDifficultyFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.DifficultyWithVision),
                    getQuestionConfig(props.questionConfigs, questionIds.DifficultyWithVisionFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.FearHeights),
                    getQuestionConfig(props.questionConfigs, questionIds.FearHeightsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.UnderlyingMentalHealthConditions),
                    getQuestionConfig(props.questionConfigs, questionIds.UnderlyingMentalHealthConditionsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.ImpairedAwareness),
                    getQuestionConfig(props.questionConfigs, questionIds.ImpairedAwarenessFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.TakingRegularMedication),
                    getQuestionConfig(props.questionConfigs, questionIds.TakingRegularMedicationFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.MedicationSideEffects),
                    getQuestionConfig(props.questionConfigs, questionIds.MedicationSideEffectsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.AlcoholDependency),
                    getQuestionConfig(props.questionConfigs, questionIds.AlcoholDependencyFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.DrugDependency),
                    getQuestionConfig(props.questionConfigs, questionIds.DrugDependencyFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.MoreThan14UnitsAlcoholAWeek),
                    getQuestionConfig(props.questionConfigs, questionIds.MoreThan14UnitsAlcoholAWeekFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.NotifyDvlaMedicalConditions),
                    getQuestionConfig(props.questionConfigs, questionIds.NotifyDvlaMedicalConditionsFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.AreYouReceivingAnyFormOfMedicalTreatment),
                    getQuestionConfig(props.questionConfigs, questionIds.AreYouReceivingAnyFormOfMedicalTreatmentDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.DoYouConsiderYourselfToBeInGoodHealth),
                    getQuestionConfig(props.questionConfigs, questionIds.DoYouConsiderYourselfToBeInGoodHealthDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.DoYouSufferFromAnyFormOfDisabilityImpairment),
                    getQuestionConfig(props.questionConfigs, questionIds.DoYouSufferFromAnyFormOfDisabilityImpairmentDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            {YesNoWithFollowUpQuestion({
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.ConditionAttentionOccupationalHealthTeam),
                    getQuestionConfig(props.questionConfigs, questionIds.ConditionAttentionOccupationalHealthTeamFurtherDetails)
                ],
                readonly: props.readonly,
                questions: props.questions
            })}
            <CheckboxQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(
                    props.questionConfigs,
                    questionIds.UnderstandCompleteAnnualSelfDeclarationToContinueWorkingAsPitPupDriver
                )}
                value="Confirmed"
                mandatory
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.IfAnsweredYesToAnyQuestionsAreSymptomsOngoingForLongerThan12Months)}
                mandatory
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(
                    props.questionConfigs,
                    questionIds.ConfirmThereHasBeenNoChangeToVisionOrOngoingMedicalConditionSinceLastF2FPitPupAssessment
                )}
                mandatory
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AreAnySymptomsOrMedicationNewWithinLast12Months)}
                mandatory
            />
            <TextQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PitPupMedicalsFurtherDetails)}
                multiline
                mandatory={areFurtherDetailsMandatory}
                hideDivider
            />
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const AssociateMedicalDetailsSection = connect(makeMapStateToQuestionnaireSectionProps(sectionId), mapDispatchToQuestionnaireSectionProps)(AssociateMedicalDetailsSectionUnconnected);
